'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '../lib/supabase/client';

const REMEMBER_KEY = 'sb_remember_me';

export function getRememberMe(): boolean {
  if (typeof window === 'undefined') return true;
  const val = localStorage.getItem(REMEMBER_KEY);
  return val === null ? true : val === '1';
}

export function setRememberMe(value: boolean) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(REMEMBER_KEY, value ? '1' : '0');
}

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<'user' | 'admin' | 'editor'>('user');
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [waitlistIds, setWaitlistIds] = useState<Set<string>>(new Set());
  const supabase = createClient();

  const loadFavorites = useCallback(async (userId: string) => {
    try {
      const { data } = await supabase
        .from('favorites')
        .select('offer_id')
        .eq('user_id', userId);
      if (data) {
        setFavoriteIds(new Set(data.map((f: any) => f.offer_id)));
      }
    } catch {}
  }, [supabase]);

  const loadWaitlists = useCallback(async (userId: string) => {
    try {
      const { data } = await supabase
        .from('waitlist_members')
        .select('waitlist_id')
        .eq('user_id', userId);
      if (data) {
        setWaitlistIds(new Set(data.map((w: any) => w.waitlist_id)));
      }
    } catch {}
  }, [supabase]);

  // Load role directly from profiles table — always reflects the DB value
  const loadUserRole = useCallback(async (userId: string) => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .single();
      if (data?.role) {
        setUserRole(data.role as 'user' | 'admin' | 'editor');
      } else {
        setUserRole('user');
      }
    } catch {
      setUserRole('user');
    }
  }, [supabase]);

  useEffect(() => {
    // ── Startup: verify localStorage token, refresh if missing/expired ──
    const initSession = async () => {
      try {
        // First try to get the existing session (reads from localStorage via our adapter)
        const { data: { session: existingSession } } = await supabase.auth.getSession();

        if (existingSession) {
          // Session found — check if the access token is still valid (not expired)
          const expiresAt = existingSession.expires_at ?? 0;
          const nowSec = Math.floor(Date.now() / 1000);
          const isExpired = expiresAt > 0 && expiresAt - nowSec < 60; // refresh if < 60s left

          if (isExpired) {
            // Token is about to expire or already expired — force a refresh
            const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
            if (!refreshErr && refreshed.session) {
              setSession(refreshed.session);
              setUser(refreshed.session.user);
              if (refreshed.session.user) {
                loadFavorites(refreshed.session.user.id);
                loadWaitlists(refreshed.session.user.id);
                loadUserRole(refreshed.session.user.id);
              }
            } else {
              // Refresh failed — clear state but don't force logout (let user stay on current page)
              setSession(null);
              setUser(null);
            }
          } else {
            setSession(existingSession);
            setUser(existingSession.user);
            if (existingSession.user) {
              loadFavorites(existingSession.user.id);
              loadWaitlists(existingSession.user.id);
              loadUserRole(existingSession.user.id);
            }
          }
        } else {
          // No session in localStorage — attempt a silent refresh using stored refresh_token
          try {
            const stored = localStorage.getItem('sb_session');
            if (stored) {
              const parsed = JSON.parse(stored);
              if (parsed?.refresh_token) {
                const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession({
                  refresh_token: parsed.refresh_token,
                });
                if (!refreshErr && refreshed.session) {
                  setSession(refreshed.session);
                  setUser(refreshed.session.user);
                  if (refreshed.session.user) {
                    loadFavorites(refreshed.session.user.id);
                    loadWaitlists(refreshed.session.user.id);
                    loadUserRole(refreshed.session.user.id);
                  }
                  setLoading(false);
                  return;
                }
              }
            }
          } catch {}
          setSession(null);
          setUser(null);
        }
      } catch {
        // Fallback: try getSession one more time
        try {
          const { data: { session: fallback } } = await supabase.auth.getSession();
          setSession(fallback);
          setUser(fallback?.user ?? null);
          if (fallback?.user) {
            loadFavorites(fallback.user.id);
            loadWaitlists(fallback.user.id);
            loadUserRole(fallback.user.id);
          }
        } catch {
          setSession(null);
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    initSession();

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        loadFavorites(session.user.id);
        loadWaitlists(session.user.id);
        loadUserRole(session.user.id);
      } else {
        setFavoriteIds(new Set());
        setWaitlistIds(new Set());
        setUserRole('user');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const toggleFavorite = useCallback(async (offerId: string): Promise<boolean> => {
    if (!user) return false;
    const isFav = favoriteIds.has(offerId);
    // Optimistic update
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(offerId);
      else next.add(offerId);
      return next;
    });
    try {
      if (isFav) {
        await supabase.from('favorites').delete().eq('user_id', user.id).eq('offer_id', offerId);
      } else {
        await supabase.from('favorites').insert({ user_id: user.id, offer_id: offerId });
      }
      return !isFav;
    } catch {
      // Revert on error
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(offerId);
        else next.delete(offerId);
        return next;
      });
      return isFav;
    }
  }, [user, favoriteIds, supabase]);

  const joinWaitlist = useCallback(async (waitlistId: string, displayName: string): Promise<boolean> => {
    if (!user) return false;
    try {
      await supabase.from('waitlist_members').insert({
        waitlist_id: waitlistId,
        user_id: user.id,
        display_name: displayName,
      });
      setWaitlistIds((prev) => new Set([...prev, waitlistId]));
      return true;
    } catch {
      return false;
    }
  }, [user, supabase]);

  const leaveWaitlist = useCallback(async (waitlistId: string): Promise<boolean> => {
    if (!user) return false;
    try {
      await supabase.from('waitlist_members').delete()
        .eq('waitlist_id', waitlistId)
        .eq('user_id', user.id);
      setWaitlistIds((prev) => {
        const next = new Set(prev);
        next.delete(waitlistId);
        return next;
      });
      return true;
    } catch {
      return false;
    }
  }, [user, supabase]);

  const respondWaitlist = useCallback(async (waitlistId: string, contactInfo: string): Promise<boolean> => {
    if (!user) return false;
    try {
      await supabase.from('waitlist_members')
        .update({ has_responded: true, contact_info: contactInfo })
        .eq('waitlist_id', waitlistId)
        .eq('user_id', user.id);
      return true;
    } catch {
      return false;
    }
  }, [user, supabase]);

  const signUp = async (email: string, password: string, metadata = {}) => {
    setRememberMe(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;

    // Handle referral: if a referral code was provided, record it after signup
    const referralCode = (metadata as any)?.referralCode;
    if (referralCode && data.user?.id) {
      try {
        // Fire-and-forget: record referral in background
        fetch('/api/referral/complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            referrerId: referralCode,
            referredId: data.user.id,
          }),
        }).then(() => {
          // Clear referral code from sessionStorage after use
          try { sessionStorage.removeItem('referral_code'); } catch {}
        }).catch(() => {});
      } catch {}
    }

    // Send welcome email (non-blocking, fire-and-forget)
    try {
      fetch('/api/auth/welcome-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          displayName: (metadata as any)?.fullName || 'Abenteurer',
        }),
      }).catch(() => {});
    } catch {
      // non-critical
    }

    return data;
  };

  const signIn = async (email: string, password: string, remember: boolean = true) => {
    setRememberMe(remember);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (remember && data.session) {
      try {
        localStorage.setItem('sb_session', JSON.stringify({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
          expires_at: data.session.expires_at,
        }));
      } catch {}
    }
    // Reload role after sign-in
    if (data.user) {
      await loadUserRole(data.user.id);
    }
    return data;
  };

  const signOut = async () => {
    setRememberMe(false);
    try { localStorage.removeItem('sb_session'); } catch {}
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUserRole('user');
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase.from('profiles').select('*').eq('id', user.id).single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    userRole,
    favoriteIds,
    waitlistIds,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    toggleFavorite,
    joinWaitlist,
    leaveWaitlist,
    respondWaitlist,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
